/**
 * Login Page - Neumorphic design with theme support
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useTheme, Icons } from '../lib/design-system';
import Spinner from '../components/ui/Spinner';
import SplashScreen from '../components/SplashScreen';

export default function Login() {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [showSplash, setShowSplash] = useState(false);
  const { login, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // MOJ-62: read what is actually in the fields, not only what React has
    // seen. Safari on iOS/iPadOS (and iCloud Keychain autofill) can write a
    // value into an input without firing the input event React listens to,
    // which leaves the component state stale/empty while the field looks
    // filled — the classic "correct credentials rejected on the first try,
    // works after retyping" symptom. The DOM value is the source of truth.
    const form = new FormData(e.currentTarget);
    const submittedEmail = String(form.get('email') ?? email).trim();
    const submittedPassword = String(form.get('password') ?? password);
    if (submittedEmail !== email) setEmail(submittedEmail);
    if (submittedPassword !== password) setPassword(submittedPassword);

    try {
      await login(submittedEmail, submittedPassword);
      setShowSplash(true);
    } catch (err: any) {
      setError(err.response?.data?.error || err.offlineMessage || 'Login fehlgeschlagen');
    }
  };

  if (showSplash) {
    return <SplashScreen onComplete={() => navigate('/')} />;
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 50%, ${theme.accent} 100%)`,
      }}
    >
      <div
        className="w-full max-w-md p-8 md:p-10 rounded-3xl shadow-2xl"
        style={{ backgroundColor: theme.surface }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div
            className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-neu-md"
            style={{ backgroundColor: theme.primaryLight }}
          >
            <Icons.Paw size={44} weight="duotone" style={{ color: theme.primary }} />
          </div>
          <h1
            className="text-3xl font-bold"
            style={{ color: theme.text }}
          >
            Mojo's Class
          </h1>
          <p
            className="text-sm mt-1"
            style={{ color: theme.textMuted }}
          >
            Hundetraining leicht gemacht
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email Field */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium mb-2"
              style={{ color: theme.text }}
            >
              E-Mail
            </label>
            <div className="relative">
              <div
                className="absolute left-4 top-1/2 -translate-y-1/2"
                style={{ color: theme.textMuted }}
              >
                <Icons.Envelope size={20} />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-neu pl-12"
                placeholder="deine@email.de"
                required
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="next"
                data-testid="login-email"
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium mb-2"
              style={{ color: theme.text }}
            >
              Passwort
            </label>
            <div className="relative">
              <div
                className="absolute left-4 top-1/2 -translate-y-1/2"
                style={{ color: theme.textMuted }}
              >
                <Icons.Lock size={20} />
              </div>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-neu pl-12 pr-12"
                placeholder="••••••••"
                required
                autoComplete="current-password"
                // When the password is shown as plain text, iOS would otherwise
                // capitalise the first letter and auto-correct it.
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2"
                style={{ color: theme.textMuted }}
                tabIndex={-1}
              >
                {showPassword ? <Icons.Hide size={20} /> : <Icons.View size={20} />}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div
              role="alert"
              className="p-4 rounded-xl flex items-center gap-3"
              style={{
                backgroundColor: `${theme.error}15`,
                color: theme.error,
              }}
              data-testid="login-error"
            >
              <Icons.Warning size={20} weight="fill" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3.5"
            data-testid="login-submit"
          >
            {isLoading ? (
              <>
                <Spinner size="sm" />
                <span>Anmelden...</span>
              </>
            ) : (
              <>
                <Icons.SignOut size={20} className="rotate-180" />
                <span>Anmelden</span>
              </>
            )}
          </button>
        </form>

        {/* Register Link */}
        <div className="mt-4 text-center">
          <Link
            to="/register"
            className="text-sm"
            style={{ color: theme.textMuted }}
          >
            Noch kein Konto? Jetzt registrieren
          </Link>
        </div>
      </div>
    </div>
  );
}
